// src/components/Map/MapVisualization.tsx
import React from 'react';
import { MapContainer, TileLayer, GeoJSON, LayersControl } from 'react-leaflet';
import { useMap } from '../../hooks/useMap';
import { useProcessing } from '../../context/ProcessingContext';
import { MapControlPanel } from './MapControlPanel';
import { MapLegend } from './MapLegend';
import { LatLng, GeoJSONFeature } from '../../types';
import 'leaflet/dist/leaflet.css';

const BASE_MAPS = {
 osm: {
   url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
   attribution: '© OpenStreetMap contributors',
   name: 'OpenStreetMap'
 },
 satellite: {
   url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
   attribution: '© Esri',
   name: 'Satellite'
 },
 terrain: {
   url: 'https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg',
   attribution: '© Stamen Design',
   name: 'Terrain'
 }
};

export const MapVisualization: React.FC = () => {
 const { state } = useProcessing();
 const { settings } = useMap();

 if (!state.contourGeoJSON || !state.interpolatedData?.ml) {
   return (
     <div className="panel-section">
       <h2>Interactive Map</h2>
       <p className="empty-state">Please complete the contour visualization step first.</p>
     </div>
   );
 }

 const bounds = state.rawData?.geographic_metadata.bounds_original;
 const center: LatLng = {
   lat: (bounds?.lat_min + bounds?.lat_max) / 2 || 0,
   lng: (bounds?.lon_min + bounds?.lon_max) / 2 || 0
 };

 const latSpan = bounds?.lat_max - bounds?.lat_min || 0;
 const lonSpan = bounds?.lon_max - bounds?.lon_min || 0;
 const zoom = Math.floor(Math.log2(360 / Math.max(latSpan, lonSpan))) + 1;

 const getFeatureStyle = (feature: GeoJSONFeature) => {
   const { min, max } = state.interpolatedData!.ml!.stats;
   const elevation = feature.properties.elevation;
   const normalizedElevation = (elevation - min) / (max - min);
   const hue = 240 - (normalizedElevation * 240);

   return {
     color: `hsl(${hue}, 70%, 50%)`,
     weight: settings.contourOpacity * 2,
     opacity: settings.contourOpacity,
     fillOpacity: 0 // No fill for LineString features
   };
 };

 const onEachFeature = (feature: GeoJSONFeature, layer: any) => {
   if (settings.showLabels) {
     layer.bindTooltip(
       `${feature.properties.elevation.toFixed(2)}`,
       {
         permanent: true,
         direction: 'center',
         className: 'contour-label'
       }
     );
   }
   
   layer.on({
     mouseover: (e: any) => {
       const layer = e.target;
       layer.setStyle({
         weight: 4,
         opacity: 1
       });
     },
     mouseout: (e: any) => {
       const layer = e.target;
       layer.setStyle(getFeatureStyle(feature));
     }
   });
 };

 return (
   <div className="panel-section">
     <h2>Interactive Map</h2>
     <div className="panel-content">
       <div className="map-container">
         <MapContainer
           center={center}
           zoom={zoom}
           style={{ height: '600px', width: '100%' }}
         >
           <LayersControl position="topright">
             {Object.entries(BASE_MAPS).map(([key, config]) => (
               <LayersControl.BaseLayer
                 key={key}
                 checked={settings.baseMap === key}
                 name={config.name}
               >
                 <TileLayer
                   url={config.url}
                   attribution={config.attribution}
                 />
               </LayersControl.BaseLayer>
             ))}

             <LayersControl.Overlay
               checked={true}
               name="Contours"
             >
               <GeoJSON
                 data={state.contourGeoJSON}
                 style={getFeatureStyle}
                 onEachFeature={onEachFeature}
               />
             </LayersControl.Overlay>
           </LayersControl>

           <MapLegend />
         </MapContainer>

         <MapControlPanel />
       </div>
     </div>
   </div>
 );
};

export default MapVisualization;